import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading } from "components/misc/Headings";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons";
import LocationPinIcon from '@mui/icons-material/LocationPin';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { fetchPlotImages } from "helpers/plots.js";

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-8 lg:pb-20 lg:pt-16`;
const HeadingWithControl = tw.div`flex flex-col items-center sm:items-stretch sm:flex-row justify-between lg:mx-10 mx-1 pb-4`;
const Controls = tw.div`flex items-center`;
const ControlButton = styled(PrimaryButtonBase)`
  ${tw`mt-4 sm:mt-0 first:ml-0 ml-6 rounded-full p-2`}
  svg {
    ${tw`w-6 h-6`}
  }
`;
const PrevButton = tw(ControlButton)``;
const NextButton = tw(ControlButton)``;

const CardSlider = styled(Slider)`
  ${tw`mt-16`}
  .slick-track {
    ${tw`flex`}
  }
  .slick-slide {
    ${tw`h-auto flex justify-center mb-1`}
  }
`;
const Card = tw.div`h-full flex! w-80 flex-col sm:border max-w-sm sm:rounded-tl-4xl sm:rounded-br-5xl relative focus:outline-none`;
const CardImage = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  tw`w-80 h-64 sm:w-full sm:h-64 bg-cover bg-center rounded sm:rounded-none sm:rounded-tl-4xl cursor-pointer transition-transform duration-200 hover:scale-105`
]);

const Text = tw.div`ml-2 text-sm font-semibold text-gray-800`;
const TextInfo = tw.div`py-6 sm:px-10 sm:py-6`;
const IconWithText = tw.div`flex items-center mr-6 my-2 sm:my-0`;
const IconContainer = styled.div`
  ${tw`inline-block rounded-full bg-gray-700 text-gray-100`}
  svg {
    ${tw`w-3 h-3`}
  }
`;

// Improved Modal styles
const ModalOverlay = tw.div`fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4`;
const ModalContent = tw.div`relative max-w-[95vw] max-h-[95vh] flex items-center justify-center`;
const ModalImage = styled.img`
  ${tw`rounded shadow-2xl`}
  max-width: min(90vw, 1200px);
  max-height: min(90vh, 800px);
  width: auto;
  height: auto;
  object-fit: contain;
`;
const CloseButton = tw.button`absolute top-0 right-0 m-2 text-white bg-red-600 rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-75 transition-colors duration-200`;

export default () => {
  const [sliderRef, setSliderRef] = useState(null)
  const [isZoomed, setIsZoomed] = useState(false)
  const [imgSrc, setImgSrc] = useState(null)
  const [cards, setCards] = useState([])

  useEffect(() => {
    fetchPlotImages().then(images => {
      if (Array.isArray(images)) {
        setCards(images.map(image => ({
          imageSrc: image.url,
          locationText: `Plot ${image.plot_number}`,
        })));
      }
    });
  }, []);

  const handleImageClick = (src) => {
    setIsZoomed(true);
    setImgSrc(src)
  };

  const handleCloseZoom = () => {
    setIsZoomed(false);
    setImgSrc(null)
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setIsZoomed(false);
    }
  };

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isZoomed) {
        handleCloseZoom();
      }
    };

    if (isZoomed) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isZoomed]);

  const sliderSettings = {
    arrows: false,
    slidesToShow: 5,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
        }
      },
    ]
  };

  if (cards.length === 0) {
    return null;
  }

  return (
    <Container>
      {isZoomed && (
        <ModalOverlay onClick={handleOverlayClick}>
          <ModalContent>
            <CloseButton onClick={handleCloseZoom}>
              ×
            </CloseButton>
            <ModalImage
              src={imgSrc}
              alt="Plot image - enlarged view"
            />
          </ModalContent>
        </ModalOverlay>
      )}
      <Content>
        <HeadingWithControl style={{borderBottom: "1px solid #0000001f"}}>
          <SectionHeading sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Our Homes
          </SectionHeading>
          <Controls>
            <PrevButton onClick={sliderRef?.slickPrev}><ChevronLeftIcon/></PrevButton>
            <NextButton onClick={sliderRef?.slickNext}><ChevronRightIcon/></NextButton>
          </Controls>
        </HeadingWithControl>
        <CardSlider ref={setSliderRef} {...sliderSettings}>
          {cards.map((card, index) => (
            <Card key={index}>
              <CardImage imageSrc={card.imageSrc} onClick={() => handleImageClick(card.imageSrc)}/>
              <TextInfo>
                <IconWithText>
                  <IconContainer style={{padding: '0.15rem 0.5rem'}}>
                    <LocationPinIcon />
                  </IconContainer>
                  <Text>{card.locationText}</Text>
                </IconWithText>
              </TextInfo>
            </Card>
          ))}
        </CardSlider>
      </Content>
    </Container>
  );
};
